/**
 * Accelerated aging calculation engine.
 *
 * The conventional relationships implemented here are:
 *
 *   AAF = Q10 ^ ((T_AA - T_RT) / 10)
 *   AAT = RTD / AAF
 *
 * Where:
 *   AAF  = accelerated aging factor (dimensionless)
 *   Q10  = assumed reaction-rate multiplier per 10 °C increase (an assumption)
 *   T_AA = accelerated aging temperature, °C
 *   T_RT = real-time / reference temperature, °C
 *   RTD  = desired real-time shelf-life duration, converted to days
 *   AAT  = calculated accelerated aging duration, days
 *
 * Rounding policy:
 *   - No intermediate rounding. Every intermediate value is carried at full
 *     IEEE-754 double precision.
 *   - Rounding happens only at display time, and the unrounded value is always
 *     shown alongside it.
 *   - `aatDaysWholeCeiling` is a *display convention* (ceiling to whole days),
 *     provided because study durations are usually scheduled in whole days.
 *     It is not a recommendation about study duration.
 *
 * This module contains no engineering judgement and no UI. It answers "what is
 * the arithmetic", never "is this the right thing to do".
 */

import type {
  CalculationInputs,
  CalculationResult,
  DerivationStep,
  SensitivityRow,
  TemperatureScenarioRow,
} from '../types';
import { CONVERSION_CONVENTION, addDaysToISODate, describeConversion, toDays } from './units';

/** Divisor in the exponent: the "per 10 °C" in the Q10 definition. */
export const Q10_TEMPERATURE_INTERVAL_C = 10;

export function calculateExponent(acceleratedTempC: number, referenceTempC: number): number {
  return (acceleratedTempC - referenceTempC) / Q10_TEMPERATURE_INTERVAL_C;
}

export function calculateAAF(q10: number, acceleratedTempC: number, referenceTempC: number): number {
  return Math.pow(q10, calculateExponent(acceleratedTempC, referenceTempC));
}

export function calculateAATDays(realTimeDays: number, aaf: number): number {
  return realTimeDays / aaf;
}

/** Format a number for the derivation trail without hiding precision. */
function d(value: number, maxDecimals = 6): string {
  if (!Number.isFinite(value)) return String(value);
  if (Number.isInteger(value)) return value.toString();
  const fixed = value.toFixed(maxDecimals);
  return fixed.replace(/0+$/, '').replace(/\.$/, '');
}

export function calculate(inputs: CalculationInputs): CalculationResult {
  const { realTimeDurationValue, realTimeDurationUnit, referenceTempC, acceleratedTempC, q10 } = inputs;

  const realTimeDays = toDays(realTimeDurationValue, realTimeDurationUnit);
  const exponent = calculateExponent(acceleratedTempC, referenceTempC);
  const aaf = calculateAAF(q10, acceleratedTempC, referenceTempC);
  const aatDays = calculateAATDays(realTimeDays, aaf);
  const aatDaysWholeCeiling = Number.isFinite(aatDays) ? Math.ceil(aatDays) : NaN;

  const projectedCompletionDate =
    inputs.startDate && Number.isFinite(aatDaysWholeCeiling)
      ? addDaysToISODate(inputs.startDate, aatDaysWholeCeiling)
      : undefined;

  const derivation: DerivationStep[] = [
    {
      label: 'Inputs as entered',
      substituted: [
        `Desired real-time shelf life = ${d(realTimeDurationValue)} ${realTimeDurationUnit}`,
        `Reference temperature T_RT = ${d(referenceTempC)} °C`,
        `Accelerated temperature T_AA = ${d(acceleratedTempC)} °C`,
        `Q10 assumption = ${d(q10)}`,
      ].join('\n'),
      note: 'These are the values you entered. The tool does not alter them.',
    },
    {
      label: 'Real-time duration converted to days',
      expression: 'RTD = value × days per unit',
      substituted: `RTD = ${describeConversion(realTimeDurationValue, realTimeDurationUnit)}`,
      result: `RTD = ${d(realTimeDays)} days`,
      note: CONVERSION_CONVENTION.summary,
    },
    {
      label: 'Exponent',
      expression: `exponent = (T_AA − T_RT) / ${Q10_TEMPERATURE_INTERVAL_C}`,
      substituted: `exponent = (${d(acceleratedTempC)} − ${d(referenceTempC)}) / ${Q10_TEMPERATURE_INTERVAL_C}`,
      result: `exponent = ${d(exponent)}`,
    },
    {
      label: 'Accelerated aging factor',
      expression: 'AAF = Q10 ^ exponent',
      substituted: `AAF = ${d(q10)} ^ ${d(exponent)}`,
      result: `AAF = ${d(aaf, 10)}`,
      note: 'Carried at full precision; no rounding applied at this step.',
    },
    {
      label: 'Accelerated aging duration',
      expression: 'AAT = RTD / AAF',
      substituted: `AAT = ${d(realTimeDays)} / ${d(aaf, 10)}`,
      result: `AAT = ${d(aatDays, 10)} days`,
    },
    {
      label: 'Final rounding for display',
      expression: 'whole days = ceiling(AAT)',
      substituted: `whole days = ceiling(${d(aatDays, 10)})`,
      result: Number.isFinite(aatDaysWholeCeiling) ? `${aatDaysWholeCeiling} days` : '—',
      note: 'A display convention only. The scheduled study duration is your decision, not an output of this tool.',
    },
  ];

  if (inputs.startDate) {
    derivation.push({
      label: 'Projected completion date',
      expression: 'completion = start date + ceiling(AAT) days',
      substituted: `completion = ${inputs.startDate} + ${
        Number.isFinite(aatDaysWholeCeiling) ? aatDaysWholeCeiling : '—'
      } days`,
      result: projectedCompletionDate ?? '—',
      note: 'Calendar arithmetic in UTC. It does not account for chamber loading, ramp times, holidays, or sampling points.',
    });
  }

  return {
    inputs,
    realTimeDays,
    exponent,
    aaf,
    aatDays,
    aatDaysWholeCeiling,
    projectedCompletionDate,
    derivation,
    computedAt: new Date().toISOString(),
  };
}

export const DEFAULT_Q10_SENSITIVITY_VALUES = [1.8, 2.0, 2.2, 2.5];

/**
 * Recalculate the same scenario across a set of Q10 assumptions.
 * The user's own Q10 is always included and flagged as the baseline, so the
 * comparison is anchored to what they actually entered.
 */
export function calculateQ10Sensitivity(
  inputs: CalculationInputs,
  q10Values: number[] = DEFAULT_Q10_SENSITIVITY_VALUES,
): SensitivityRow[] {
  const baseline = calculate(inputs);

  const values = Array.from(new Set([...q10Values, inputs.q10]))
    .filter((v) => Number.isFinite(v) && v > 0)
    .sort((a, b) => a - b);

  return values.map((q10) => {
    const aaf = calculateAAF(q10, inputs.acceleratedTempC, inputs.referenceTempC);
    const aatDays = calculateAATDays(baseline.realTimeDays, aaf);
    return {
      q10,
      aaf,
      aatDays,
      aatDaysWholeCeiling: Number.isFinite(aatDays) ? Math.ceil(aatDays) : NaN,
      deltaDaysVsBaseline: aatDays - baseline.aatDays,
      isBaseline: q10 === inputs.q10,
    };
  });
}

/**
 * Recalculate the same scenario across a set of accelerated temperatures.
 * Exploratory only: the tool does not order, score, or recommend these.
 */
export function calculateTemperatureScenarios(
  inputs: CalculationInputs,
  temperatures: number[],
): TemperatureScenarioRow[] {
  const baseline = calculate(inputs);

  const values = Array.from(new Set([...temperatures, inputs.acceleratedTempC]))
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);

  return values.map((acceleratedTempC) => {
    const aaf = calculateAAF(inputs.q10, acceleratedTempC, inputs.referenceTempC);
    const aatDays = calculateAATDays(baseline.realTimeDays, aaf);
    let note: string | undefined;
    if (acceleratedTempC === inputs.referenceTempC) note = 'Equals reference temperature: AAF = 1.';
    else if (acceleratedTempC < inputs.referenceTempC) note = 'Below reference temperature: AAF < 1.';
    return {
      acceleratedTempC,
      aaf,
      aatDays,
      aatDaysWholeCeiling: Number.isFinite(aatDays) ? Math.ceil(aatDays) : NaN,
      deltaDaysVsBaseline: aatDays - baseline.aatDays,
      isBaseline: acceleratedTempC === inputs.acceleratedTempC,
      note,
    };
  });
}

/** Build a default temperature sweep centred on the user's own entry. */
export function defaultTemperatureSweep(inputs: CalculationInputs, step = 5, count = 5): number[] {
  const half = Math.floor(count / 2);
  const values: number[] = [];
  for (let i = -half; i <= half; i++) {
    values.push(Number((inputs.acceleratedTempC + i * step).toFixed(4)));
  }
  return values;
}
